import _ from "underscore";

import Backbone from "pages/common/lib/backbone-helpers";

import Constants from "const/constants";

const ContextModel = Backbone.Model.extend({
    defaults: {
        coverImage: `/static/img/cards-campaign.png`,
        geoTargetingType: `whitelist`
    },

    constants: {
        grabyoDomain: `grabyo.com`,
        STATUS: {
            ACTIVE: `active`,
            ARCHIVED: `archived`
        }
    },

    initialize: function (attrs, options) {
        options = _.isObject(options) ? options : {};

        if (options.isNew) {
            this.unset(`coverImage`);
        }

        _.bindAll(
            this,
            `destroy`,
            `set`,
            `getPublicGalleryDomain`,
            `getPublicGalleryDomainWithoutPrefix`,
            `isGeotargeted`
        );
    },

    toJSON: function () {
        return _.extend(ContextModel.__super__.toJSON.apply(this, arguments), {
            id: this.getCompoundId()
        });
    },

    getCompoundId: function () {
        return `${this.get(`type`)}:${this.id}`;
    },

    generateQueryParam: function () {
        const params = {};
        if (window.App.Utils.params.streamId) {
            params.streamId = window.App.Utils.params.streamId;
        }

        if (this.idAttribute === `galleryId`) {
            params.campaignId = this.id;
        } else {
            params[this.idAttribute] = this.id;
        }

        return $.param(params);
    },

    parse: function (_attrs) {
        const attrs = { ..._attrs };
        if (attrs.inlineTypeConfig && _.isString(attrs.inlineTypeConfig)) {
            attrs.inlineTypeConfig = JSON.parse(attrs.inlineTypeConfig);
        }
        if (attrs.twitterCardConfig && _.isString(attrs.twitterCardConfig)) {
            attrs.twitterCardConfig = JSON.parse(attrs.twitterCardConfig);
        }
        if (attrs.twitterCardConfig && _.isString(attrs.twitterCardConfig)) {
            attrs.twitterCardConfig = JSON.parse(attrs.twitterCardConfig);
        }
        if (attrs.facebookPostConfig && _.isString(attrs.facebookPostConfig)) {
            attrs.facebookPostConfig = JSON.parse(attrs.facebookPostConfig);
        }
        if (attrs.renderConfig && _.isString(attrs.renderConfig)) {
            attrs.renderConfig = JSON.parse(attrs.renderConfig);
        }

        // WARNING - Here allowedCountries and disallowedCountries are checked for null.
        // Both of them should never co-exists. If there is allowedCountries should never be a disallowedCountries and vice versa.
        // This is just taking priority of the allowedCountries over DisallowedCountries.
        if (attrs.allowedCountries) {
            attrs.targetedCountries = attrs.allowedCountries;
            attrs.geoTargetingType = `whitelist`;
        } else if (attrs.disallowedCountries) {
            attrs.targetedCountries = attrs.disallowedCountries;
            attrs.geoTargetingType = `blacklist`;
        }

        return attrs;
    },

    toFormFormat: function () {
        const data = {
            id: this.id,
            compoundId: this.getCompoundId(),
            name: this.get(`name`),
            targetedCountries: this.get(`targetedCountries`),
            geoTargetingType: this.get(`geoTargetingType`),
            publicName: this.getDisplayName(),
            profileUsername: this.get(`profileUsername`),
            profileImage: this.get(`profileImageUrl`),
            siteUrl: this.get(`siteUrl`),
            googleAnalyticsTrackingId: this.get(`externalGaTrackingId`),
            type: this.get(`type`)
        };

        if (this.get(`coverImage`) !== this.defaults.coverImage) {
            data.coverImage = this.get(`coverImage`);
        }

        data.hasPlayerBradingIframe = false;

        if (this.has(`facebookPostConfig`)) {
            const facebookPostConfig = this.get(`facebookPostConfig`);
            data.defaultFacebookUserId = facebookPostConfig.defaultMentionId;
            data.defaultFacebookUserName =
                facebookPostConfig.defaultMentionName;
        }

        if (this.has(`twitterCardConfig`)) {
            const twitterCardConfig = this.get(`twitterCardConfig`);
            data.defaultTwitterUser = twitterCardConfig.site;
        }

        if (this.has(`renderConfig`) && this.get(`renderConfig`).background) {
            data.backgroundColor = this.get(`renderConfig`).background;
        }

        return data;
    },

    isDefault: function () {
        let isDefault = App.Utils.params[this.idAttribute] === this.id;

        if (!isDefault && this.idAttribute === `galleryId`) {
            isDefault = App.Utils.params[`campaignId`] === this.id;
        }

        return isDefault;
    },

    getPublicGalleryDomainWithoutPrefix: function () {
        let url = ``;
        if (
            this.has(`domainOverride`) &&
            this.get(`domainOverride`) !== null &&
            this.get(`domainOverride`) !== ``
        ) {
            url += this.get(`domainOverride`);
        } else {
            url += this.constants.grabyoDomain;

            // If we're in development, then set the domain to be our current instance
            if (
                window.data !== undefined &&
                window.data.environment === `development`
            ) {
                url = location.host;
            }
        }

        // Check if protocol is present
        if (!url.toLowerCase().match(/^http/)) {
            url = `https://${url}`;
        }

        // Check if the domain doesn't contain a trailing slash
        if (!url.endsWith(`/`)) {
            url += `/`;
        }

        url += `g/`;

        return url;
    },

    getPublicGalleryDomain: function () {
        let url = this.getPublicGalleryDomainWithoutPrefix();

        if (
            !this.has(`aliasId`) ||
            this.get(`aliasId`) === null ||
            this.get(`aliasId`) === ``
        ) {
            url += `${this.constants.urlPrefix}/`;
        }

        return url;
    },

    getPublicContextUrl: function () {
        let url = this.getPublicGalleryDomain();

        if (
            this.has(`aliasId`) &&
            this.get(`aliasId`) !== null &&
            this.get(`aliasId`) !== ``
        ) {
            url += this.get(`aliasId`);
        } else {
            url += this.id;
        }

        return url;
    },

    getName: function () {
        return this.get(`name`) ? this.get(`name`) : this.id;
    },

    getDisplayName: function () {
        return this.get(`displayName`)
            ? this.get(`displayName`)
            : this.get(`name`);
    },

    isGeotargeted: function () {
        const hasAllowedCountries = this.get(`allowedCountries`)?.length > 0;
        const hasDisallowedCountries =
            this.get(`disallowedCountries`)?.length > 0;
        return (
            hasAllowedCountries ||
            hasDisallowedCountries ||
            !_.isEmpty(this.get(`targetedCountries`))
        );
    },

    updateGallery: function (data) {
        const props = {
            url: this.url(),
            type: `PUT`,
            data: JSON.stringify(data)
        };
        if (this.url().includes(Constants.DEFAULT_API_PATH)) {
            Object.assign(props, {
                processData: false,
                contentType: `application/json`
            });
        }
        $.ajax(props).done((response) => {
            response = response.data || response;
            this.set(response);
        });
    },

    toggleStatus: function () {
        let newStatus;
        if (this.get(`status`) === this.constants.STATUS.ACTIVE) {
            newStatus = this.constants.STATUS.ARCHIVED;
        } else {
            newStatus = this.constants.STATUS.ACTIVE;
        }

        this.updateGallery({
            contextStatus: newStatus
        });
    },

    getExtension: function () {
        return new Promise((res, rej) => {
            this.fetch({ data: { extended: true } })
                .then((response) => res(response.galleryExtension))
                .catch(rej);
        });
    },

    hasExtension: function () {
        return this.has(`galleryExtension`);
    },

    getEncodingProfile: function () {
        return this.get(`encodingProfile`);
    }
});

export default ContextModel;
